import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { productsAPI, currencyAPI } from '../services/api';
import { getStoredRole, getStoredToken } from '../utils/auth';

const ProductContext = createContext(null);

const normalizeProduct = (product = {}) => ({
    id: String(product.id || product._id || ''),
    name: product.name || '',
    logoUrl: product.logoUrl || product.logo || '',
    code: product.code || '',
    platform: product.platform || 'BOTH',
    isActive: product.isActive !== false
});

const getStoredSelectedProduct = () => localStorage.getItem('selectedProduct') || '';
const getStoredCurrency = () => localStorage.getItem('preferredCurrency') || 'INR';

const fetchUsdRate = async () => {
    try {
        const res = await currencyAPI.getLatestRate();
        const rate = res?.data?.data?.rate;
        return typeof rate === 'number' && rate > 0 ? rate : 84;
    } catch {
        return 84;
    }
};

export const ProductProvider = ({ children }) => {
    const [products, setProducts] = useState([]);
    const [selectedProductId, setSelectedProductId] = useState(getStoredSelectedProduct());
    const [loadingProducts, setLoadingProducts] = useState(true);
    const [currency, setCurrency] = useState(getStoredCurrency); // 'INR' | 'USD'
    const [usdRate, setUsdRate] = useState(84); // INR per 1 USD
    const [userRole, setUserRole] = useState(() => getStoredRole() || '');
    const [userEmail, setUserEmail] = useState(() => localStorage.getItem('userEmail') || '');

    // Fetch live exchange rate once on mount
    useEffect(() => {
        fetchUsdRate().then(setUsdRate);
    }, []);

    const toggleCurrency = useCallback(() => {
        setCurrency((prev) => {
            const next = prev === 'INR' ? 'USD' : 'INR';
            localStorage.setItem('preferredCurrency', next);
            return next;
        });
    }, []);

    const isSuperAdmin = userRole === 'SUPER_ADMIN';

    const selectProduct = useCallback((productId) => {
        const normalizedProductId = String(productId || '');
        setSelectedProductId(normalizedProductId);

        if (normalizedProductId) {
            localStorage.setItem('selectedProduct', normalizedProductId);
        } else {
            localStorage.removeItem('selectedProduct');
        }
    }, []);

    const clearSelectedProduct = useCallback(() => {
        setSelectedProductId('');
        localStorage.removeItem('selectedProduct');
    }, []);

    const refreshProducts = useCallback(async () => {
        const token = getStoredToken();
        const currentRole = getStoredRole() || '';
        const currentEmail = localStorage.getItem('userEmail') || '';

        // Sync role and email into React state so components re-render after login
        setUserRole(currentRole);
        setUserEmail(currentEmail);

        if (!token) {
            setProducts([]);
            setLoadingProducts(false);
            clearSelectedProduct();
            return;
        }

        setLoadingProducts(true);

        try {
            const response = await productsAPI.list(currentRole === 'SUPER_ADMIN' ? { includeInactive: 'true' } : undefined);
            const fetchedProducts = (response.data?.data || []).map(normalizeProduct).filter((product) => product.id);
            const fetchedIds = new Set(fetchedProducts.map((product) => product.id));

            setProducts(fetchedProducts);

            const persistedSelection = getStoredSelectedProduct();

            if (currentRole === 'PRODUCT_USER') {
                if (fetchedProducts.length === 1) {
                    selectProduct(fetchedProducts[0].id);
                } else if (persistedSelection && fetchedIds.has(persistedSelection)) {
                    selectProduct(persistedSelection);
                } else {
                    clearSelectedProduct();
                }
            } else if (persistedSelection && fetchedIds.has(persistedSelection)) {
                selectProduct(persistedSelection);
            } else {
                clearSelectedProduct();
            }
        } catch (error) {
            setProducts([]);
            clearSelectedProduct();
            window.showToast(error.response?.data?.message || 'Failed to load products', 'error');
        } finally {
            setLoadingProducts(false);
        }
    }, [clearSelectedProduct, selectProduct]);

    useEffect(() => {
        refreshProducts();
    }, [refreshProducts]);

   
    const selectedProduct = useMemo(
        () => products.find((product) => product.id === selectedProductId) || null,
        [products, selectedProductId]
    );

    const canAccessProduct = useCallback((productId) => {
        if (!productId) {
            return false;
        }

        if (isSuperAdmin) {
            return true;
        }

        return products.some((product) => product.id === String(productId));
    }, [isSuperAdmin, products]);

    const value = {
        userRole,
        isSuperAdmin,
        userEmail,
        products,
        selectedProductId,
        selectedProduct,
        loadingProducts,
        selectProduct,
        clearSelectedProduct,
        canAccessProduct,
        refreshProducts,
        currency,
        usdRate,
        toggleCurrency,
    };

    return (
        <ProductContext.Provider value={value}>
            {children}
        </ProductContext.Provider>
    );
};

export const useProductContext = () => {
    const context = useContext(ProductContext);

    if (!context) {
        throw new Error('useProductContext must be used within ProductProvider');
    }

    return context;
};
